import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Input, Modal, Select } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../../lib/format';
import { toast } from '../../lib/toast';
import { norm } from '../../components/nav';
import { askConfirm, askNumber } from '../../components/dialogs';
import { useMe } from '../auth/api';
import { useMaterials } from '../materials/api';
import { PHASE_TYPE_LABEL } from '../phases/types';
import {
  itemLabel,
  sourceFor,
  useAddPurchase,
  useCloseReceipt,
  useDeletePurchase,
  useFillLowStock,
  useMarkBought,
  usePurchases,
  useReceivePurchase,
  useShops,
  useUpdatePurchase,
  waitingQty,
  type PurchaseItem,
} from './api';

/**
 * Nákupní seznam po obchodech. V obchodě v telefonu: odškrtnout „koupeno"
 * a rovnou naskladnit (počet; nákupní cenu zadá finance — jinak za průměr).
 * Objednané z e-shopu čeká na doručení a přijímá se i po částech (tady nebo skenem).
 */

const num = (s: string) => Number(String(s).replace(',', '.'));

// Poslední známá cena se předvyplní, ať se neopisuje; dodací list ji může změnit.
const lastPrice = (item: PurchaseItem, canPrice: boolean) => {
  const src = sourceFor(item);
  return canPrice && src?.unitCost ? { value: String(Number(src.unitCost)), date: src.priceDate } : null;
};

function BoughtDialog({ item, canPrice, onClose }: { item: PurchaseItem; canPrice: boolean; onClose: () => void }) {
  const last = lastPrice(item, canPrice);
  const [qty, setQty] = useState(String(Number(item.qty)));
  const [price, setPrice] = useState(last?.value ?? '');
  const bought = useMarkBought();
  const submit = (receive: boolean) =>
    bought.mutate(
      {
        id: item.id,
        receiveQty: receive && item.material ? num(qty) || undefined : undefined,
        unitCost: receive && canPrice && price.trim() ? num(price) : undefined,
      },
      {
        onSuccess: () => {
          toast.success(!item.material ? `Koupeno: ${itemLabel(item)}` : receive ? `Koupeno a naskladněno: ${itemLabel(item)}` : `Objednáno, čeká na doručení: ${itemLabel(item)}`);
          onClose();
        },
        onError: (e) => toast.error((e as ApiError).message),
      },
    );
  return (
    <Modal title={`Koupeno — ${itemLabel(item)}`} onClose={onClose}>
      <div className="space-y-3">
        {item.material ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="mb-1 text-xs text-zinc-500">Přivezeno ({item.material.unit})</div>
                <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 text-lg" />
              </div>
              {canPrice && (
                <div>
                  <div className="mb-1 text-xs text-zinc-500">Cena / {item.material.unit} bez DPH</div>
                  <Input inputMode="decimal" placeholder="nepovinné" value={price} onChange={(e) => setPrice(e.target.value)} className="h-12 text-lg" />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500">
              {item.material.stockLocation && <>Pozice {item.material.stockLocation.code}. </>}
              {canPrice
                ? last ? `Cena je poslední známá${last.date ? ` z ${fmtDate(last.date)}` : ''}, uprav ji podle dokladu.` : 'S cenou se přepočítá průměr a uloží poslední cena u obchodu.'
                : 'Naskladní se za průměrnou cenu; nákupní cenu doplní finance.'}
              {' '}Co nepřivezeš, počká v Čeká na doručení.
            </p>
          </>
        ) : (
          <p className="text-sm text-zinc-500">Volná položka (není ve skladu) — jen se odškrtne.</p>
        )}
        {item.material ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Button className="h-12" disabled={bought.isPending || !(num(qty) > 0)} onClick={() => submit(true)}>Koupeno a naskladnit</Button>
            <Button variant="secondary" className="h-12" disabled={bought.isPending} onClick={() => submit(false)}>Objednáno, dorazí později</Button>
          </div>
        ) : (
          <Button className="h-12 w-full" disabled={bought.isPending} onClick={() => submit(false)}>{bought.isPending ? 'Ukládám…' : 'Potvrdit'}</Button>
        )}
      </div>
    </Modal>
  );
}

// Doručení: i po částech, s rychlou volbou po baleních, jak zboží opravdu chodí.
function ReceiveDialog({ item, canPrice, onClose }: { item: PurchaseItem; canPrice: boolean; onClose: () => void }) {
  const left = waitingQty(item);
  const src = sourceFor(item);
  const pack = src?.packQty ? Number(src.packQty) : 0;
  const packs = pack > 0 ? Array.from({ length: Math.min(4, Math.ceil(left / pack)) }, (_, i) => i + 1) : [];
  const last = lastPrice(item, canPrice);
  const [qty, setQty] = useState(String(left));
  const [price, setPrice] = useState(last?.value ?? '');
  const receive = useReceivePurchase();
  const closeReceipt = useCloseReceipt();
  const unit = item.material?.unit ?? item.unit ?? '';
  const onError = (e: unknown) => toast.error((e as ApiError).message);
  const submit = () =>
    receive.mutate(
      { id: item.id, qty: num(qty), unitCost: canPrice && price.trim() ? num(price) : undefined },
      {
        onSuccess: (r) => {
          const rest = waitingQty(r);
          toast.success(rest > 0 ? `Přijato, čeká ještě ${fmtNumber(rest)} ${unit}` : `Přijato celé: ${itemLabel(item)}`);
          onClose();
        },
        onError,
      },
    );
  const giveUp = async () => {
    const ok = await askConfirm({ title: 'Zbytek nedorazí?', body: `${fmtNumber(left)} ${unit} se přestane čekat. Co už dorazilo, zůstane na skladě i v historii.`, confirmLabel: 'Nedorazí' });
    if (ok) closeReceipt.mutate(item.id, { onSuccess: () => { toast.success('Hotovo, na zbytek se nečeká'); onClose(); }, onError });
  };
  return (
    <Modal title={`Přijmout — ${itemLabel(item)}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-zinc-500">
          Objednáno {fmtNumber(Number(item.qty))} {unit}
          {Number(item.receivedQty ?? 0) > 0 && <>, dorazilo {fmtNumber(Number(item.receivedQty))} {unit}</>}
          {item.material?.stockLocation && <> · pozice {item.material.stockLocation.code}</>}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="mb-1 text-xs text-zinc-500">Dorazilo teď ({unit})</div>
            <Input inputMode="decimal" autoFocus value={qty} onChange={(e) => setQty(e.target.value)} onFocus={(e) => e.target.select()} className="h-12 text-lg" />
          </div>
          {canPrice && (
            <div>
              <div className="mb-1 text-xs text-zinc-500">Cena / {unit} bez DPH</div>
              <Input inputMode="decimal" placeholder="nepovinné" value={price} onChange={(e) => setPrice(e.target.value)} className="h-12 text-lg" />
            </div>
          )}
        </div>
        {packs.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {packs.map((n) => (
              <button key={n} type="button" onClick={() => setQty(String(Math.round(n * pack * 1000) / 1000))} className="min-h-9 rounded-full border border-zinc-300 px-3 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800">
                {n}× {src?.packLabel ?? `balení (${fmtNumber(pack)} ${unit})`}
              </button>
            ))}
          </div>
        )}
        {last && <p className="text-xs text-zinc-500">Cena je poslední známá{last.date ? ` z ${fmtDate(last.date)}` : ''}, uprav ji podle dokladu.</p>}
        <Button className="h-12 w-full" disabled={receive.isPending || !(num(qty) > 0)} onClick={submit}>{receive.isPending ? 'Ukládám…' : 'Přijmout na sklad'}</Button>
        <button type="button" disabled={closeReceipt.isPending} onClick={giveUp} className="w-full text-center text-sm text-zinc-500 underline">Zbytek nedorazí</button>
      </div>
    </Modal>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const { data: materials } = useMaterials();
  const { data: shops } = useShops();
  const add = useAddPurchase();
  const [q, setQ] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [name, setName] = useState('');
  const [qty, setQty] = useState('1');
  const [shopId, setShopId] = useState('');
  const matches = useMemo(() => {
    const t = norm(q);
    return t ? (materials ?? []).filter((m) => norm(`${m.name} ${m.sku}`).includes(t)).slice(0, 8) : [];
  }, [q, materials]);
  const chosen = materials?.find((m) => m.id === materialId);
  const submit = () =>
    add.mutate(
      { materialId: materialId || undefined, name: materialId ? undefined : name.trim() || q.trim(), qty: num(qty), shopId: shopId || undefined },
      { onSuccess: () => { toast.success('Přidáno do nákupu'); onClose(); }, onError: (e) => toast.error((e as ApiError).message) },
    );
  return (
    <Modal title="Přidat do nákupu" onClose={onClose}>
      <div className="space-y-3">
        {chosen ? (
          <div className="flex items-center justify-between rounded-lg bg-zinc-50 p-2 text-sm dark:bg-zinc-900">
            <span>{chosen.name} <span className="font-mono text-xs text-zinc-400">{chosen.sku}</span></span>
            <button onClick={() => setMaterialId('')} className="text-xs text-zinc-500 underline">změnit</button>
          </div>
        ) : (
          <>
            <Input autoFocus placeholder="Materiál ze skladu (hledej) nebo napiš, co koupit" value={q} onChange={(e) => { setQ(e.target.value); setName(e.target.value); }} />
            {matches.length > 0 && (
              <div className="space-y-1">
                {matches.map((m) => (
                  <button key={m.id} onClick={() => { setMaterialId(m.id); setQ(''); }} className="block w-full rounded-lg border border-zinc-200 px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800">
                    {m.name} <span className="font-mono text-xs text-zinc-400">{m.sku}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-[7rem_1fr] gap-2">
          <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} aria-label="Množství" />
          <Select value={shopId} onChange={(e) => setShopId(e.target.value)} aria-label="Obchod">
            <option value="">{chosen ? 'obchod podle zdroje' : '— obchod —'}</option>
            {(shops ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
        <Button className="w-full" disabled={add.isPending || !(num(qty) > 0) || (!materialId && !(name.trim() || q.trim()))} onClick={submit}>Přidat</Button>
      </div>
    </Modal>
  );
}

export default function PurchasePage() {
  const { data: me } = useMe();
  const canPrice = !!me?.caps.includes('finance');
  const [tab, setTab] = useState<'open' | 'waiting' | 'bought'>('open');
  const { data: items, isLoading } = usePurchases(tab);
  const { data: waiting } = usePurchases('waiting');
  const fill = useFillLowStock();
  const update = useUpdatePurchase();
  const del = useDeletePurchase();
  const [boughtFor, setBoughtFor] = useState<PurchaseItem | null>(null);
  const [receiveFor, setReceiveFor] = useState<PurchaseItem | null>(null);
  const [adding, setAdding] = useState(false);
  const onError = (e: unknown) => toast.error((e as ApiError).message);

  // Po obchodech; bez obchodu na konci.
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; shop: PurchaseItem['shop']; items: PurchaseItem[] }>();
    for (const p of items ?? []) {
      const key = p.shop?.id ?? '—';
      const g = map.get(key) ?? { name: p.shop?.name ?? 'Bez obchodu', shop: p.shop, items: [] };
      g.items.push(p);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => (!a.shop ? 1 : !b.shop ? -1 : a.name.localeCompare(b.name, 'cs')));
  }, [items]);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Nákup</h1>
          <p className="text-sm text-zinc-500">Co koupit, po obchodech. V obchodě odškrtni a rovnou naskladni, objednané počká na doručení.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={fill.isPending} onClick={() => fill.mutate(undefined, { onSuccess: (r) => toast.success(r.added ? `Přidáno ${r.added} položek pod minimem` : 'Nic pod minimem, co by už nebylo v seznamu'), onError })}>
            Doplnit pod minimem
          </Button>
          <Button onClick={() => setAdding(true)}>+ Přidat</Button>
        </div>
      </div>

      <div className="mb-3 inline-flex max-w-full gap-0.5 overflow-x-auto rounded-lg border border-zinc-200 bg-zinc-100/70 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/70">
        {([['open', 'Koupit'], ['waiting', `Čeká na doručení${waiting?.length ? ` (${waiting.length})` : ''}`], ['bought', 'Koupeno']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={tab === id ? 'whitespace-nowrap rounded-md bg-white px-3 py-1 text-sm font-medium shadow-sm dark:bg-zinc-800' : 'whitespace-nowrap rounded-md px-3 py-1 text-sm text-zinc-500'}>{label}</button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}
      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 dark:border-zinc-700">
          {tab === 'bought' ? 'Zatím nic koupeno.' : tab === 'waiting' ? 'Na nic se nečeká, všechno objednané dorazilo.' : 'Seznam je prázdný. Přidej položku, nebo doplň materiál pod minimem.'}
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => (
          <section key={g.shop?.id ?? 'bez'} className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex flex-wrap items-baseline gap-x-3 border-b border-zinc-100 px-4 py-2 dark:border-zinc-800">
              <span className="font-semibold">{g.name}</span>
              <span className="text-xs text-zinc-500">{g.items.length} {g.items.length === 1 ? 'položka' : g.items.length <= 4 ? 'položky' : 'položek'}</span>
              {g.shop?.customerNo && <span className="text-xs text-zinc-500">zák. č. {g.shop.customerNo}</span>}
              {g.shop?.web && <a href={g.shop.web} target="_blank" rel="noreferrer" className="ml-auto text-xs text-sky-700 underline dark:text-sky-400">web</a>}
            </div>
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {g.items.map((p) => {
                const src = sourceFor(p);
                const packs = src?.packQty && Number(src.packQty) > 0 ? Math.ceil(Number(p.qty) / Number(src.packQty)) : null;
                const left = waitingQty(p);
                return (
                  <li key={p.id} className="flex items-start gap-3 px-4 py-3">
                    {p.status === 'open' ? (
                      <button onClick={() => setBoughtFor(p)} className="mt-0.5 h-7 w-7 shrink-0 rounded-md border-2 border-zinc-300 hover:border-emerald-500 dark:border-zinc-600" aria-label="Koupeno" />
                    ) : left > 0 ? (
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Čeká na doručení">⏳</span>
                    ) : (
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-500 text-white">✓</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium leading-tight">
                        {itemLabel(p)} <span className="tabular-nums text-zinc-600 dark:text-zinc-300">· {fmtNumber(Number(p.qty))} {p.unit ?? ''}</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-zinc-500">
                        {packs && src?.packLabel && <span>{packs}× {src.packLabel}</span>}
                        {src?.shopSku && <span className="font-mono">{src.shopSku}</span>}
                        {canPrice && src?.unitCost && <span>{fmtMoney(Number(src.unitCost), src.currency)}/{p.unit} ({src.priceDate ? fmtDate(src.priceDate) : '—'})</span>}
                        {p.material && <span>skladem {fmtNumber(Number(p.material.stockQty))}{p.material.minQty ? ` / min ${fmtNumber(Number(p.material.minQty))}` : ''}</span>}
                        {p.material?.stockLocation && <span>→ {p.material.stockLocation.code}</span>}
                        {p.phase && (
                          <Link to="/vyroby/$propItemId" params={{ propItemId: p.phase.propItem.id }} className="text-sky-700 underline dark:text-sky-400">
                            {p.phase.propItem.project.code} · {p.phase.propItem.name} → {PHASE_TYPE_LABEL[p.phase.type]}
                          </Link>
                        )}
                        {p.note && <span>· {p.note}</span>}
                        {p.status === 'bought' && <span className="text-emerald-700 dark:text-emerald-400">koupil {p.boughtBy?.fullName ?? '?'} {p.boughtAt ? fmtDate(p.boughtAt) : ''}{p.receivedQty ? `, naskladněno ${fmtNumber(Number(p.receivedQty))}` : ''}</span>}
                        {left > 0 && <span className="font-medium text-amber-700 dark:text-amber-400">čeká {fmtNumber(left)} {p.unit ?? ''}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {left > 0 && <Button variant="secondary" className="h-9" onClick={() => setReceiveFor(p)}>Přijmout</Button>}
                      {src?.url && <a href={src.url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline dark:text-sky-400">e-shop</a>}
                      {p.status === 'open' && (
                        <button
                          onClick={async () => {
                            const v = await askNumber({ title: `Množství — ${itemLabel(p)}`, initial: String(Number(p.qty)), label: p.unit ?? undefined });
                            if (v && v > 0) update.mutate({ id: p.id, qty: v }, { onError });
                          }}
                          className="text-xs text-zinc-500 underline"
                        >
                          množství
                        </button>
                      )}
                      <button onClick={() => del.mutate(p.id, { onError })} className="text-zinc-400 hover:text-red-600" title="Odebrat ze seznamu">×</button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {boughtFor && <BoughtDialog item={boughtFor} canPrice={canPrice} onClose={() => setBoughtFor(null)} />}
      {receiveFor && <ReceiveDialog item={receiveFor} canPrice={canPrice} onClose={() => setReceiveFor(null)} />}
      {adding && <AddDialog onClose={() => setAdding(false)} />}
    </div>
  );
}
